// Loose shape check (something@something.tld): enough to catch typos and half-typed input without
// rejecting unusual but valid addresses. The server remains the authority on what's deliverable.
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value);
}

type generateEmailLinkParams = {
  to?: string;
  subject?: string;
  body?: string;
};

/**
 * Generates a mailto link with the given parameters.
 *
 * All parameters are optional, if all are empty it will just open their default email client and create a new email with nothing prefilled.
 */
export const generateEmailLink = ({ to = "", subject, body }: generateEmailLinkParams): string => {
  const emailSubject = subject ? `subject=${encodeURIComponent(subject)}` : "";
  const emailBody = body ? `body=${encodeURIComponent(body)}` : "";
  if (!subject && !body) {
    return `mailto:${to}`;
  }
  if (subject && body) {
    return `mailto:${to}?${emailSubject}&${emailBody}`;
  }
  if (!body) {
    return `mailto:${to}?${emailSubject}`;
  }
  if (!subject) {
    return `mailto:${to}?${emailBody}`;
  }
  throw new Error("Invalid arguments for generateEmailLink");
};

/**
 * Personal (non-work) email domains we refuse to create new accounts for.
 * Got from here: https://knowledge.hubspot.com/forms/what-domains-are-blocked-when-using-the-forms-email-domains-to-block-feature
 * This is the client-side gate: (preventing a form submission) and doesn't need to be exhaustive
 * The backend sibling is a superset of this list: auth-service/src/auth/disallowed-email-domains.ts
 */
const DISALLOWED_DOMAINS = [
  "alibaba.com",
  "aol.com",
  "arcticmail.com",
  "argentina.com",
  "aristotle.org",
  "berlin.com",
  "berlin.de",
  "btconnect.co.uk",
  "btconnect.com",
  "btinternet.com",
  "btopenworld.co.uk",
  "cable.comcast.com",
  "canada.com",
  "canadianmail.com",
  "china.com",
  "co.uk",
  "comcast.net",
  "dublin.com",
  "dublin.ie",
  "duck.com",
  "facebook.com",
  "fake-mail.cf",
  "fake-mail.ga",
  "fake-mail.ml",
  "fastmail.ca",
  "fastmail.cn",
  "fastmail.co.uk",
  "fastmail.com",
  "freemail.de",
  "freemail.et",
  "freemail.gr",
  "freemail.hu",
  "freemail.it",
  "freemail.lt",
  "freemail.ms",
  "freemail.nl",
  "gmail.com",
  "gmail.com.br",
  "gmail.ru",
  "googlemail.com",
  "goplay.com",
  "gorillaswithdirtyarmpits.com",
  "hongkong.com",
  "hotmail.ca",
  "hotmail.ch",
  "hotmail.co",
  "hotmail.co.il",
  "hotmail.co.jp",
  "hotmail.co.nz",
  "hotmail.co.uk",
  "hotmail.co.za",
  "hotmail.com",
  "hotmail.com.au",
  "hotmail.com.br",
  "hotmail.com.tr",
  "hotmail.de",
  "hotmail.es",
  "hotmail.fi",
  "hotmail.fr",
  "hotmail.it",
  "hotmail.kg",
  "hotmail.kz",
  "hotmail.nl",
  "hotmail.ru",
  "hotmail.se",
  "icloud.com",
  "inbox.com",
  "inbox.net",
  "inbox.ru",
  "inbox.si",
  "japan.com",
  "junkmail.com",
  "junkmail.gq",
  "live.at",
  "live.be",
  "live.ca",
  "live.cl",
  "live.cn",
  "live.co.uk",
  "live.co.za",
  "live.com",
  "live.com.ar",
  "live.com.au",
  "live.com.mx",
  "live.com.pt",
  "live.com.sg",
  "live.de",
  "live.dk",
  "live.fr",
  "live.ie",
  "live.in",
  "live.it",
  "live.jp",
  "live.nl",
  "live.no",
  "live.ru",
  "live.se",
  "london.com",
  "mac.com",
  "mail.bg",
  "mail-awu.de",
  "mail-box.cz",
  "mail-center.com",
  "mail-central.com",
  "mail-easy.fr",
  "mail-filter.com",
  "mail-me.com",
  "mail-page.com",
  "mail-tester.com",
  "mail.austria.com",
  "mail.az",
  "mail.be",
  "mail.bulgaria.com",
  "mail.by",
  "mail.co.za",
  "mail.com",
  "mail.com.tr",
  "mail.de",
  "mail.ee",
  "mail.md",
  "mail.nu",
  "mail.org.uk",
  "mail.pf",
  "mail.pt",
  "mail.ru",
  "mail.sisna.com",
  "mail.svenz.eu",
  "mail.usa.com",
  "msn.cn",
  "msn.com",
  "msn.nl",
  "newmail.com",
  "newmail.net",
  "newmail.ru",
  "newyork.com",
  "o2.co.uk",
  "oninet.pt",
  "online.ie",
  "online.ms",
  "online.nl",
  "outlook.at",
  "outlook.be",
  "outlook.cl",
  "outlook.co.id",
  "outlook.co.il",
  "outlook.co.nz",
  "outlook.co.th",
  "outlook.com",
  "outlook.com.au",
  "outlook.com.br",
  "outlook.com.gr",
  "outlook.com.pe",
  "outlook.com.tr",
  "outlook.com.vn",
  "outlook.cz",
  "outlook.de",
  "outlook.dk",
  "outlook.es",
  "outlook.fr",
  "outlook.hu",
  "outlook.ie",
  "outlook.in",
  "outlook.it",
  "outlook.jp",
  "outlook.kr",
  "outlook.lv",
  "outlook.my",
  "outlook.nl",
  "outlook.ph",
  "outlook.pt",
  "outlook.sa",
  "outlook.sg",
  "outlook.sk",
  "protestant.com",
  "protonmail.com",
  "plus.com",
  "plus.google.com",
  "postmark.net",
  "secure-mail.biz",
  "secure-mail.cc",
  "sky.com",
  "temp-mail.com",
  "temp-mail.de",
  "uol.com.ar",
  "uol.com.br",
  "uol.com.co",
  "uol.com.mx",
  "uol.com.ve",
  "usa.com",
  "usa.net",
  "virgin.net",
  "virginbroadband.com.au",
  "virginmedia.com",
  "virtualmail.com",
  "vodafone.nl",
  "yaho.com",
  "yahoo.ae",
  "yahoo.at",
  "yahoo.be",
  "yahoo.ca",
  "yahoo.ch",
  "yahoo.cn",
  "yahoo.co",
  "yahoo.co.id",
  "yahoo.co.il",
  "yahoo.co.in",
  "yahoo.co.jp",
  "yahoo.co.kr",
  "yahoo.co.nz",
  "yahoo.co.th",
  "yahoo.co.uk",
  "yahoo.co.za",
  "yahoo.com",
  "yahoo.com.ar",
  "yahoo.com.au",
  "yahoo.com.br",
  "yahoo.com.cn",
  "yahoo.com.co",
  "yahoo.com.hk",
  "yahoo.com.is",
  "yahoo.com.mx",
  "yahoo.com.my",
  "yahoo.com.ph",
  "yahoo.com.ru",
  "yahoo.com.sg",
  "yahoo.com.tr",
  "yahoo.com.tw",
  "yahoo.com.vn",
  "yahoo.cz",
  "yahoo.de",
  "yahoo.dk",
  "yahoo.es",
  "yahoo.fi",
  "yahoo.fr",
  "yahoo.gr",
  "yahoo.hu",
  "yahoo.ie",
  "yahoo.in",
  "yahoo.it",
  "yahoo.jp",
  "yahoo.nl",
  "yahoo.no",
  "yahoo.pl",
  "yahoo.pt",
  "yahoo.ro",
  "yahoo.ru",
  "yahoo.se",
  "yomail.info",
  "yopmail.com",
  "your-mail.com",
  "yourinbox.com",
];

/**
 *  Same domain matching as the backend gate (auth-service/src/auth/disallowed-email-domains.ts):
 * split on `@`, normalise the domain, and match it exactly against the blocklist. Unlike the
 * backend (a hard gate that throws), this runs live in form validation, so anything that isn't a
 * well-formed "local@domain" returns false — email format is left to the input's browser validation.
 * @param email
 * @returns
 */
export function isPersonalEmailDomain(email: string): boolean {
  const splitEmail = email.split("@");
  if (splitEmail.length !== 2) {
    return false;
  }
  const domain = splitEmail[1]?.toLowerCase().trim();
  if (!domain) {
    return false;
  }
  return DISALLOWED_DOMAINS.includes(domain);
}
